import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Eye,
  Loader2,
  RefreshCcw,
  Search,
  Shield,
  ShieldCheck,
  ShieldX,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import {
  demoteSuperadmin,
  getMe,
  impersonate,
  listUsers,
  promoteSuperadmin,
} from "@/api/auth";
import { useAuthStore } from "@/stores/auth.store";
import { useDebounce } from "@/hooks/useDebounce";
import type { User } from "@/api/types";

const PAGE_SIZE_FOR_FETCH = 200;

export function SuperadminsSection() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const setCurrentUser = useAuthStore((s) => s.setUser);
  const setTokens = useAuthStore((s) => s.setTokens);

  const [superadmins, setSuperadmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [demoteTarget, setDemoteTarget] = useState<User | null>(null);
  const [demoting, setDemoting] = useState(false);

  const [impersonateTarget, setImpersonateTarget] = useState<User | null>(null);
  const [impersonating, setImpersonating] = useState(false);

  const existingIds = useMemo(
    () => new Set(superadmins.map((u) => u.id)),
    [superadmins],
  );

  async function handleImpersonate() {
    if (!impersonateTarget) return;
    setImpersonating(true);
    try {
      const tokens = await impersonate(impersonateTarget.id);
      setTokens({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type,
      });
      const me = await getMe();
      setCurrentUser(me);
      toast.success(`Signed in as ${impersonateTarget.full_name}`);
      setImpersonateTarget(null);
      navigate("/projects", { replace: true });
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setImpersonating(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      // Walk all pages until we've drained the list — superadmins are rare
      // so this finishes in a single request in practice.
      const collected: User[] = [];
      let page = 1;
      // Use a generous page size to keep the round-trip count low.
       
      while (true) {
        const res = await listUsers({
          page,
          page_size: PAGE_SIZE_FOR_FETCH,
        });
        collected.push(...res.items.filter((u) => u.is_superadmin));
        if (res.items.length < res.page_size) break;
        if (page * res.page_size >= res.total) break;
        page += 1;
      }
      setSuperadmins(
        collected.sort((a, b) => a.full_name.localeCompare(b.full_name)),
      );
    } catch (err) {
      setErrorMessage(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDemote() {
    if (!demoteTarget) return;
    setDemoting(true);
    try {
      const updated = await demoteSuperadmin(demoteTarget.id);
      setSuperadmins((prev) => prev.filter((u) => u.id !== demoteTarget.id));
      // If the demoted user is the current user, refresh the local auth state
      // so the sidebar's "Superadmin" label and permissions go away immediately.
      if (currentUser?.id === updated.id) setCurrentUser(updated);
      toast.success(`${demoteTarget.full_name} is no longer a superadmin`);
      setDemoteTarget(null);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setDemoting(false);
    }
  }

  function handlePromoted(user: User) {
    setSuperadmins((prev) => {
      if (prev.some((u) => u.id === user.id)) return prev;
      return [...prev, user].sort((a, b) =>
        a.full_name.localeCompare(b.full_name),
      );
    });
    if (currentUser?.id === user.id) setCurrentUser(user);
  }

  return (
    <TooltipProvider delayDuration={150}>
      <section className="space-y-5">
        <header className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-medium tracking-tight text-slate-900">
              Superadmins
            </h2>
            <p className="text-sm text-slate-500">
              Superadmins have full access to every project on the server.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
            >
              <RefreshCcw
                className={
                  loading
                    ? "mr-2 h-3.5 w-3.5 animate-spin"
                    : "mr-2 h-3.5 w-3.5"
                }
              />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setPromoteOpen(true)}
              
            >
              <Shield className="mr-2 h-3.5 w-3.5" />
              Promote to superadmin
            </Button>
          </div>
        </header>

        <div className="rounded-plane bg-white shadow-plane">
          {loading ? (
            <div className="flex items-center justify-center px-5 py-10 text-sm text-slate-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading superadmins…
            </div>
          ) : errorMessage ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-rose-700">
                Couldn&rsquo;t load superadmins
              </p>
              <p className="mt-1 text-xs text-rose-600">{errorMessage}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={load}
                className="mt-3"
              >
                Retry
              </Button>
            </div>
          ) : superadmins.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              No superadmins on the server.
            </div>
          ) : (
            <ul className="divide-y divide-slate-900/[0.06]">
              {superadmins.map((u) => {
                const isSelf = currentUser?.id === u.id;
                const isLast = superadmins.length === 1;
                const removeDisabled = isSelf || isLast;
                const removeReason = isSelf
                  ? "You can't remove your own superadmin rights"
                  : isLast
                    ? "At least one superadmin must remain"
                    : null;
                return (
                  <li key={u.id} className="flex items-center gap-4 px-5 py-3">
                    <Avatar className="h-9 w-9 border border-slate-200">
                      <AvatarFallback className="bg-indigo-100 text-xs font-semibold text-indigo-700">
                        {initials(u.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-slate-900">
                          {u.full_name}
                        </span>
                        {isSelf && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                            You
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {u.email}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-700">
                      <ShieldCheck className="h-3 w-3" />
                      Superadmin
                    </span>
                    {removeDisabled ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0}>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled
                              className={cn(
                                "border-rose-200 text-rose-600",
                                "cursor-not-allowed opacity-50",
                              )}
                            >
                              <ShieldX className="mr-2 h-3.5 w-3.5" />
                              Remove
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="max-w-[220px] text-xs"
                        >
                          {removeReason}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setDemoteTarget(u)}
                        className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      >
                        <ShieldX className="mr-2 h-3.5 w-3.5" />
                        Remove
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <AllUsersList
          currentUserId={currentUser?.id ?? null}
          superadminIds={existingIds}
          onAskImpersonate={(u) => setImpersonateTarget(u)}
        />

        <PromoteDialog
          open={promoteOpen}
          onOpenChange={setPromoteOpen}
          existingIds={existingIds}
          onPromoted={handlePromoted}
        />

        <Dialog
          open={!!impersonateTarget}
          onOpenChange={(o) => {
            if (impersonating) return;
            if (!o) setImpersonateTarget(null);
          }}
        >
          <DialogContent className="sm:max-w-[460px]">
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <Eye className="h-5 w-5" />
              </div>
              <DialogTitle className="text-center text-lg">
                Sign in as this user?
              </DialogTitle>
              <DialogDescription className="text-center text-sm leading-relaxed">
                {impersonateTarget && (
                  <>
                    You&rsquo;ll see the app exactly as{" "}
                    <span className="font-medium text-slate-900">
                      {impersonateTarget.full_name}
                    </span>{" "}
                    sees it, with their permissions. You can switch back to
                    your own account at any time from the warning banner.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setImpersonateTarget(null)}
                disabled={impersonating}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleImpersonate}
                disabled={impersonating}
              variant="primary"
              >
                {impersonating ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Eye className="mr-2 h-3.5 w-3.5" />
                )}
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!demoteTarget}
          onOpenChange={(o) => {
            if (demoting) return;
            if (!o) setDemoteTarget(null);
          }}
        >
          <DialogContent className="sm:max-w-[460px]">
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                <ShieldX className="h-5 w-5" />
              </div>
              <DialogTitle className="text-center text-lg">
                Remove superadmin?
              </DialogTitle>
              <DialogDescription className="text-center text-sm leading-relaxed">
                {demoteTarget && (
                  <>
                    Strip superadmin rights from{" "}
                    <span className="font-medium text-slate-900">
                      {demoteTarget.full_name}
                    </span>
                    ? They keep their per-project roles and access, only the
                    server-wide override is removed.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDemoteTarget(null)}
                disabled={demoting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDemote}
                disabled={demoting}
              >
                {demoting && (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Remove superadmin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </TooltipProvider>
  );
}

// ─── Promote dialog ─────────────────────────────────────────────────────────

interface PromoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingIds: ReadonlySet<string>;
  onPromoted: (user: User) => void;
}

function PromoteDialog({
  open,
  onOpenChange,
  existingIds,
  onPromoted,
}: PromoteDialogProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const [users, setUsers] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<User | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset on open / close.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setUsers([]);
      setPicked(null);
      setPickerOpen(false);
    }
  }, [open]);

  // Search users when the picker is open.
  useEffect(() => {
    if (!pickerOpen) return;
    let cancelled = false;
    setSearching(true);
    listUsers({ q: debouncedSearch || undefined, page_size: 25 })
      .then((res) => {
        if (cancelled) return;
        setUsers(res.items);
      })
      .catch(() => !cancelled && setUsers([]))
      .finally(() => !cancelled && setSearching(false));
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, debouncedSearch]);

  async function handlePromote() {
    if (!picked) return;
    setSubmitting(true);
    try {
      const updated = await promoteSuperadmin(picked.id);
      onPromoted(updated);
      toast.success(`${updated.full_name} is now a superadmin`);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Promote a user to superadmin</DialogTitle>
          <DialogDescription>
            Pick the user who should gain server-wide access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className="h-10 w-full justify-between rounded-lg border-slate-200 bg-white font-normal"
              >
                {picked ? (
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-slate-900">
                      {picked.full_name}
                    </span>
                    <span className="truncate text-xs text-slate-400">
                      {picked.email}
                    </span>
                  </span>
                ) : (
                  <span className="text-slate-400">
                    Search for a user by name or email…
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 text-slate-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] p-0"
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search users…"
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  <CommandEmpty>
                    {searching ? "Searching…" : "No matching user."}
                  </CommandEmpty>
                  <CommandGroup>
                    {users.map((u) => {
                      const alreadySuper = existingIds.has(u.id);
                      return (
                        <CommandItem
                          key={u.id}
                          value={`${u.full_name} ${u.email}`}
                          disabled={alreadySuper}
                          onSelect={() => {
                            if (alreadySuper) return;
                            setPicked(u);
                            setPickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-3.5 w-3.5",
                              picked?.id === u.id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm">
                              {u.full_name}
                            </div>
                            <div className="truncate text-xs text-slate-400">
                              {u.email}
                            </div>
                          </div>
                          {alreadySuper && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-indigo-500">
                              Already
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs leading-relaxed text-amber-900">
              Superadmins have access to{" "}
              <span className="font-semibold">every project</span> and
              <span className="font-semibold"> every permission</span>. This
              change is global, not limited to the current project.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handlePromote}
            disabled={submitting || !picked}
          variant="primary"
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Promote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── All users list (impersonation) ────────────────────────────────────────

interface AllUsersListProps {
  currentUserId: string | null;
  superadminIds: ReadonlySet<string>;
  onAskImpersonate: (user: User) => void;
}

function AllUsersList({
  currentUserId,
  superadminIds,
  onAskImpersonate,
}: AllUsersListProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listUsers({
        q: debouncedSearch || undefined,
        page_size: 50,
      });
      setUsers(res.items);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-3 pt-2">
      <div>
        <h3 className="text-sm font-medium tracking-tight text-slate-900">
          All users
        </h3>
        <p className="text-xs text-slate-500">
          Pick any non-superadmin user to sign in as them. The action is
          logged on the server.
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 rounded-lg border-slate-200 bg-white pl-9"
        />
      </div>

      <div className="rounded-plane bg-white shadow-plane">
        {loading ? (
          <div className="flex items-center justify-center px-5 py-8 text-sm text-slate-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Searching…
          </div>
        ) : users.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">
            No users match this search.
          </div>
        ) : (
          <ul className="divide-y divide-slate-900/[0.06]">
            {users.map((u) => {
              const isSelf = currentUserId === u.id;
              const isSuper = superadminIds.has(u.id) || u.is_superadmin;
              const blocked = isSelf || isSuper;
              return (
                <li key={u.id} className="flex items-center gap-4 px-5 py-3">
                  <Avatar className="h-9 w-9 border border-slate-200">
                    <AvatarFallback className="bg-indigo-100 text-xs font-semibold text-indigo-700">
                      {initials(u.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-slate-900">
                        {u.full_name}
                      </span>
                      {isSelf && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                          You
                        </span>
                      )}
                      {isSuper && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                          <ShieldCheck className="h-2.5 w-2.5" />
                          Superadmin
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-slate-500">
                      {u.email}
                    </div>
                  </div>
                  {blocked ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled
                            className="cursor-not-allowed opacity-50"
                          >
                            <Eye className="mr-2 h-3.5 w-3.5" />
                            Sign in as
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="left"
                        className="max-w-[220px] text-xs"
                      >
                        {isSelf
                          ? "You can't impersonate yourself."
                          : "Superadmins can't be impersonated."}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onAskImpersonate(u)}
                    >
                      <Eye className="mr-2 h-3.5 w-3.5" />
                      Sign in as
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
